import { Task } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/task.pb';
import { QueryKey, useInfiniteQuery, UseInfiniteQueryResult, QueryFunctionContext } from 'react-query';
import { DEFAULT_TASK_LIMIT_WIDGETS } from '../../constants';
import { SchemaTaskCenterV2 } from '../../service';
import { IO } from '../../types';
import { defaultOptions } from '../config';
import { queryRequestKeys } from '../keys';

type WidgetTaskListPage = {
  rows: Task[];
  totalTasksCount?: number;
};

type UseGetWidgetTaskListResults<E = unknown> = UseInfiniteQueryResult<WidgetTaskListPage, E> & {
  queryKey: QueryKey;
};

/**
 * Get tasks list for widget view with time window filtering.
 * Tasks are sorted by priority (high to low) and filtered by due date.
 */
export const useGetWidgetTasksList = <E = unknown>(
  req: Omit<IO.GetWidgetTaskList['input'], 'pagination'>,
  options?: { enabled?: boolean }
): UseGetWidgetTaskListResults<E> => {
  const queryKey = queryRequestKeys.widgetTasksList(req);
  const query = useInfiniteQuery<WidgetTaskListPage, E>({
    queryKey,
    queryFn: async ({ pageParam }: QueryFunctionContext<QueryKey, Partial<IO.GetWidgetTaskList['input']>>) => {
      const response = await SchemaTaskCenterV2.ListWidgetTasks({
        ...req,
        pagination: {
          limit: DEFAULT_TASK_LIMIT_WIDGETS,
          ...(pageParam?.pagination && { ...pageParam.pagination }),
        },
      });
      return {
        rows: response.tasks ?? [],
        totalTasksCount: response.totalTasksCount,
      };
    },
    getNextPageParam: (lastPage, allPages): Partial<IO.GetWidgetTaskList['input']> | undefined => {
      if (!lastPage.rows.length || lastPage.rows.length < DEFAULT_TASK_LIMIT_WIDGETS) return undefined;

      const offset = allPages.length * DEFAULT_TASK_LIMIT_WIDGETS;
      return { pagination: { offset } };
    },
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};
